<template>
  <div>
    <table
      class="text-grey-800 bg-white w-full shadow-lg"
    >
      <thead class="text-sm border-b-2 border-grey-300">
        <tr>
          <th
            class="px-4 py-2 text-left"
            width="100%"
          >
            Assessment
          </th>
          <th class="px-4 py-2">
            Completed
          </th>
          <th class="px-4 py-2">
            Total
          </th>
          <th v-if="includesTrlMrl" class="px-4 py-2 text-xs">
            <abbr title="Technology Readiness Level/Manufacturing Readiness Level Criteria Met">TRL/MRL</abbr>
          </th>
          <th v-if="booster" class="px-4 py-2 text-xs">
            Booster
          </th>
          <th v-if="includesRecommendedField">
            Recommended
          </th>
          <th class="px-4 py-2 text-xs">
            Min
          </th>
          <th class="px-4 py-2 text-xs">
            Max
          </th>
          <th class="px-4 py-2 text-xs">
            Range
          </th>
        </tr>
      </thead>
      <transition-group
        name="flip-list"
        tag="tbody"
      >
        <template v-for="(assessment, index) in sortedAssessments">
          <league-table-entry
            :key="assessment.uuid"
            :assessment="assessment"
            :position="++index"
            :show-trl-mrl="includesTrlMrl"
            :show-recommended="includesRecommendedField"
            :booster="booster"
            @entry:details="openModal"
          />
          <!-- eslint-disable-next-line -->
          <tr
            v-if="breakPosition && index === breakPosition"
            key="break"
          >
            <th
              :colspan="includesRecommendedField ? 7 : 6"
              class="stripes-primary border-t border-b border-primary-300 h-3"
            >
            </th>
          </tr>
        </template>
      </transition-group>
    </table>
    <portal to="modals">
      <league-table-entry-modal
        v-if="modalOpen"
        :assessments="sortedAssessments"
        :assessment="modalAssessment"
        :show-recommended="includesRecommendedField"
        :booster="booster"
        @close="closeModal"
        @entry:change="changeModal"
      />
    </portal>
  </div>
</template>

<script>
import { includes } from 'lodash';


export default {
  name: "LeagueTable",
  props: {
    applications: {
      type: [Array],
      required: true
    },
    breakPosition: {
      type: [Number, String],
      default: ""
    },
    streams: {
      type: [Array],
      default: () => []
    },
  },
  data() {
    return {
      selectedStatus: "",
      loading: false,
      modalOpen: false,
      modalAssessment: null, // set to null!
      assessments: [],
      selected: []
    };
  },
  computed: {
    isContinuationPhase() {
      return !!this.$page.marking_phase.continuation_of_marking_phase_id
    },
    includesRecommendedField() {
      return this.$page.marking_phase?.template?.listing_fields.includes('recommended') ?? false
    },
    includesTrlMrl() {
      return this.$page.marking_phase?.template?.listing_fields.includes('trl_mrl') ?? false
    },
    booster() {
      const booster = this.$page.marking_phase?.template?.booster;
      return booster ? booster : null;
    },
    templateMaxScore() {
      return this.$page.marking_phase.template?.max_score ?? 0
    },
    assessmentsByStream() {
      if(this.streams.length == 0) return this.assessments
      return this.assessments.filter(a => this.streams.includes(a.stream))
    },
    approvedAssessments() {
      return this.toBucket( this.assessmentsByStream.filter(a => a.pivot.approved_at) )
    },
    rejectedAssessments() {
      return this.toBucket( this.assessmentsByStream.filter(a => a.pivot.rejected_at) )
    },
    undecidedAssessments() {
      return this.toBucket( this.assessmentsByStream.filter(a => !a.pivot.confirmed_at) )
    },
    sortedAssessments() {
      return [
        ...this.approvedAssessments,
        ...this.undecidedAssessments,
        ...this.rejectedAssessments,
      ]
    }
  },
  created() {
    // eslint-disable-next-line
    Echo.private(`league-table.${this.fundingId}`).listen(
      ".league-table-updated",
      ({ data }) => this.fetchAssessmentUpdate(data)
    );

    this.assessments = this.applications.map(this.assignScore).map(this.assignPreviousScore)
    // this.modalAssessment = this.sortedAssessments[0]
    // this.modalOpen = true
  },
  methods: {
    assignScore(assessment) {
      const additionalKeys = {
        totalScore: 0,
        totalPercentage: 0,
        recommendedTotal: 0,
      }

      for (let index = 0; index < assessment.all_assessor_grades_current_phase.length; index++) {
        const currentAssessor = assessment.all_assessor_grades_current_phase[index];
        additionalKeys.totalScore += currentAssessor.total
        additionalKeys.totalPercentage += currentAssessor.percentage
        if(currentAssessor.recommended) additionalKeys.recommendedTotal++;
      }

      additionalKeys.totalPercentage = parseFloat((additionalKeys.totalPercentage / assessment.assessors.length).toFixed(2))

      return {
        ...assessment,
        ...additionalKeys
      }
    },

    assignPreviousScore(assessment) {
      if(!assessment.all_assessor_grades_previous_phase) return assessment

      const additionalKeys = {
        previousTotalScore: 0,
        previousTotalPercentage: 0,
        previousRecommendedTotal: 0,
      }

      for (let index = 0; index < assessment.all_assessor_grades_previous_phase.length; index++) {
        const currentAssessor = assessment.all_assessor_grades_previous_phase[index];
        additionalKeys.previousTotalScore += currentAssessor.total
        additionalKeys.previousTotalPercentage += currentAssessor.total
        if(currentAssessor.recommended) additionalKeys.previousRecommendedTotal++;
      }

      additionalKeys.previousTotalPercentage = parseFloat((additionalKeys.previousTotalPercentage / assessment.assessors.length).toFixed(2))

      return {
        ...assessment,
        ...additionalKeys
      }
    },

    // PREVIOUS SORT METHOD FOR REF
    // ============================
    // sortByRecommendedAndPercentageSort(a, b) {
    //   const compareTotalAssessors = a.assessors.length + b.assessors.length
    //   const compareRecommendedTotal = a.recommendedTotal + b.recommendedTotal

    //   if(this.includesRecommendedField) {
    //     if(compareRecommendedTotal < compareTotalAssessors - 2) {
    //       if(a.recommendedTotal < b.recommendedTotal) return 1
    //       if(a.recommendedTotal > b.recommendedTotal) return -1
    //     }
    //   }

    //   if(a.totalPercentage < b.totalPercentage) return 1
    //   if(a.totalPercentage > b.totalPercentage) return -1

    //   return 0

    // },

    sortByPercentage(a, b) {
      return a.totalPercentage < b.totalPercentage ? 1 : a.totalPercentage > b.totalPercentage ? -1 : 0
    },

    toBucket(assessments) {
      // If there's no recommended field, just sort by percentage
      if(!this.includesRecommendedField) return assessments.sort(this.sortByPercentage)
      return [
        assessments.filter(a => a.recommendedTotal > 3).sort(this.sortByPercentage),
        assessments.filter(a => a.recommendedTotal === 3).sort(this.sortByPercentage),
        assessments.filter(a => a.recommendedTotal < 3).sort(this.sortByPercentage),
      ].flat();

    },

    fetchAssessmentUpdate(entry) {
      axios
        .get(
          route("assessments.all-assessor-grades", { application: entry.uuid })
        )
        .then(({ data }) => this.updateAssessmentEntry(data));
    },
    updateAssessmentEntry(entry) {
      const assessmentIndex = this.assessments.findIndex(
        assessment => entry.uuid === assessment.uuid
      );
      if (assessmentIndex === -1) return;
      this.assessments[assessmentIndex] = Object.assign(
        this.assessments[assessmentIndex].data,
        entry
      );

    },

    openModal(uuid) {
      this.modalAssessment = this.assessments.find(assessment => assessment.uuid === uuid) ?? null;
      if(this.modalAssessment) {
        this.modalOpen = true;
      }
    },
    changeModal(uuid) {
      this.modalAssessment = this.assessments.find(assessment => assessment.uuid === uuid);
    },
    closeModal() {
      this.modalAssessment = null;
      this.modalOpen = false;
    },

  }
};
</script>
<style>
.flip-list-move {
  transition: transform 0.7s;
}
</style>
